// Reactive application state (Svelte 5 runes) on top of the framework-free Store.
// - datasets: the live 2026 bucket (/data/, polled) and static replays (/data/replay/<id>/, loaded once),
//   selected by ?replay=<id> in the URL (never remembered: a fresh visit always lands on live)
// - view: which snapshot the whole UI renders (live: latest or ?at=; replay: ?at= or the first snapshot)
// - player: a virtual clock for replaying the night (time-based, at a "night speed" like 60×)
// - route (hash), area filter (URL + localStorage), theme

import type { Meta, Overview, Timeline } from '../types';
import { Store, type StaleInfo } from './store';
import { electionYear, isPreResults } from './race';
import { holdScroll } from './anchor';
import {
  datasetBase, DEFAULT_SPEED, indexAtOrBefore, playbackStep, pointTimes, prefetchIds, REPLAY_DATASETS, replayFromQuery,
  stepIndex, switchDatasetHash, withHashParam, type Speed,
} from './timeline';

export type RouteName = 'home' | 'state' | 'race' | 'turnout' | 'diagnostics' | 'vote';
export interface Route {
  name: RouteName;
  id?: string;
  query: URLSearchParams;
}

export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#/, '') || '/';
  const [path = '/', qs = ''] = raw.split('?');
  const parts = path.split('/').filter(Boolean).map((p) => decodeURIComponent(p));
  const query = new URLSearchParams(qs);
  switch (parts[0]) {
    case 'sc':
    case 'statewide': return { name: 'state', query };
    case 'race': return { name: 'race', id: parts[1], query };
    case 'turnout': return { name: 'turnout', query };
    case 'diagnostics': return { name: 'diagnostics', query };
    case 'vote': return { name: 'vote', query };
    default: return { name: 'home', query };
  }
}

type Theme = 'auto' | 'light' | 'dark';
const THEME_KEY = 'chsvotes-theme';
const AREA_KEY = 'chsvotes-area';
const ls = {
  get(k: string) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string | null) { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* private mode */ } },
};

/** One data source: a Store plus its reactive meta/timeline. */
class DataSet {
  meta = $state.raw<Meta | null>(null);
  timeline = $state.raw<Timeline | null>(null);
  poll = $state(0);
  metaFailures = $state(0);
  stale = $state.raw<StaleInfo | null>(null);
  /** the snapshot the whole page renders: advances only once the view's core files for a new snapshot loaded */
  shownSid = $state<string | null>(null);
  /** the last attempt to load a newer snapshot failed (the page stays on shownSid and says so) */
  loadFailed = $state(false);
  private started = false;
  private retryDelay = 5_000;
  private retryTimer = 0;

  constructor(readonly id: string | null, readonly store: Store) {}

  start() {
    if (this.started) return;
    this.started = true;
    this.store.subscribe((e) => {
      // whatever this poll changes (banners, status, a new snapshot) must not move the reader
      if (app.data === this) holdScroll();
      this.meta = this.store.meta;
      this.metaFailures = this.store.metaFailures;
      this.stale = this.store.staleInfo;
      this.poll++;
      if (e.snapshotChanged || (!this.timeline && this.store.meta?.snapshot_id)) void this.loadTimeline();
    });
    this.store.start();
  }

  private tlSeq = 0;
  private async loadTimeline() {
    const my = ++this.tlSeq;
    try {
      const t = await this.store.timeline();
      if (my !== this.tlSeq) return;          // a newer load started: don't overwrite it with this older answer
      if (t) { this.timeline = t; this.noteOk(); }
    } catch {
      this.noteError(); // keep the last good timeline; retry
    }
  }

  /**
   * Something this dataset's view needs failed to load. Live data re-checks on every meta poll anyway; a static
   * replay never polls, so schedule a retry with backoff (5 s → 10 s → 20 s → 30 s) that re-runs every resource.
   */
  noteError() {
    if (!this.store.isStatic && this.meta) return;
    if (this.retryTimer) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = 0;
      this.retryDelay = Math.min(30_000, this.retryDelay * 2);
      if (!this.timeline && this.meta?.snapshot_id) void this.loadTimeline();
      this.poll++;
    }, this.retryDelay);
  }
  noteOk() {
    this.retryDelay = 5_000;
  }
}

const live = new DataSet(null, new Store(datasetBase(null)));

/** Polls close for the 2026 general election: Tue Nov 3, 2026, 7:00 PM ET (EST) — used when the live meta.json doesn't
 *  say (missing, or written before meta.election.polls_close existed). */
export const POLLS_CLOSE_2026 = '2026-11-04T00:00:00Z';
const replays = new Map<string, DataSet>();
function replaySet(id: string): DataSet {
  let d = replays.get(id);
  if (!d) {
    d = new DataSet(id, new Store(datasetBase(id), { sharedBase: datasetBase(null) }));
    replays.set(id, d);
  }
  return d;
}

class App {
  route = $state.raw<Route>(parseRoute(location.hash));
  theme = $state<Theme>((ls.get(THEME_KEY) as Theme) || 'auto');
  /** area filter (area_id) for the Charleston lists; URL wins over localStorage */
  area = $state<string | null>(parseRoute(location.hash).query.get('area') ?? ls.get(AREA_KEY));
  playing = $state(false);
  speed = $state<Speed>(DEFAULT_SPEED);
  /** virtual playback clock (ms); null = show the time of the snapshot being viewed */
  clock = $state<number | null>(null);
  /** server-corrected "now" (ms), refreshed by a ticker for relative times */
  now = $state(Date.now());
  /** replay datasets that actually exist on the server (probed once) */
  availableReplays = $state<string[]>([]);
  /** server-corrected "now" (ms) ticking every second until polls close (drives the countdown), then frozen */
  tick = $state(Date.now());


  /** active dataset id from the URL (?replay=2024); null = live */
  replay = $derived(replayFromQuery(this.route.query));
  data = $derived(this.replay ? replaySet(this.replay) : live);
  /** When polls close for the live dataset (ms): meta.election.polls_close, else 7 PM ET Nov 3 for the 2026 election
   *  (also before meta.json has loaded). null for anything else (old/mock datasets without the field). */
  pollsClose = $derived.by(() => {
    const m = live.meta;
    const v = m?.election.polls_close ? Date.parse(m.election.polls_close) : NaN;
    if (Number.isFinite(v)) return v;
    return !m || electionYear(m) === 2026 ? Date.parse(POLLS_CLOSE_2026) : null;
  });
  /** Live dataset before polls close: nothing can be "out of date" yet, so stale / feed-health warnings stay hidden. */
  preClose = $derived(!this.replay && this.pollsClose !== null && this.tick < this.pollsClose);
  get store(): Store { return this.data.store; }
  get meta() { return this.data.meta; }
  get timeline() { return this.data.timeline; }
  get poll() { return this.data.poll; }
  get metaFailures() { return this.data.metaFailures; }
  get stale() { return this.data.stale; }
  get shownSid() { return this.data.shownSid; }
  get loadFailed() { return this.data.loadFailed; }
  /** time (ms) of the snapshot actually on screen */
  shownTime = $derived.by(() => {
    const p = this.points.find((x) => x.snapshot_id === this.shownSid);
    return p ? Date.parse(p.t) : this.viewTime;
  });

  /** newest snapshot of the active dataset */
  liveSid = $derived(this.meta?.snapshot_id ?? null);
  /** requested replay position (null = default) */
  at = $derived(this.route.query.get('at'));
  points = $derived(this.timeline?.points ?? []);
  times = $derived(pointTimes(this.points));
  viewSid = $derived.by(() => {
    const at = this.at;
    if (at && (!this.timeline || this.points.some((p) => p.snapshot_id === at))) return at;
    // a link made before a dataset was rebuilt names the snapshot by its timestamp alone
    // ("20241106T011000Z" for "20241106T011000Z-b2611012225"): match the id by that prefix
    if (at) {
      const stamp = at.split('-')[0]!;
      for (let i = this.points.length - 1; i >= 0; i--) if (this.points[i]!.snapshot_id.startsWith(stamp)) return this.points[i]!.snapshot_id;
    }
    // a replay starts at the beginning of the night; live shows the latest
    if (this.replay) return this.points[0]?.snapshot_id ?? null;
    return this.liveSid;
  });
  /** true only for the live dataset at its newest snapshot */
  isLive = $derived(!this.replay && (!this.at || this.at === this.liveSid || this.viewSid === this.liveSid));
  /** The live dataset once the night is over (meta.static): final results, replayable like a recorded night. */
  final = $derived(!this.replay && !!this.meta?.static);
  /** time (ms) of the snapshot being viewed */
  viewTime = $derived.by(() => {
    const p = this.points.find((x) => x.snapshot_id === this.viewSid);
    if (p) return Date.parse(p.t);
    return this.meta?.last_snapshot_publish ? Date.parse(this.meta.last_snapshot_publish) : null;
  });
  /** index of the viewed snapshot in the timeline (-1 if unknown) */
  viewIndex = $derived.by(() => {
    const pts = this.points;
    if (!pts.length) return -1;
    const i = pts.findIndex((p) => p.snapshot_id === this.viewSid);
    if (i >= 0) return i;
    return this.viewTime === null ? pts.length - 1 : Math.max(0, indexAtOrBefore(this.times, this.viewTime));
  });
  /** what the player shows as "now": the running clock, else the viewed snapshot's time */
  displayTime = $derived(this.clock ?? this.viewTime);
  /** replay reached the last snapshot */
  atEnd = $derived(this.points.length > 1 && this.viewIndex === this.points.length - 1 && (this.clock === null || this.clock >= (this.times.at(-1) ?? 0)));

  private raf = 0;
  private last = 0;

  start() {
    live.start();
    if (this.replay) replaySet(this.replay).start();
    window.addEventListener('hashchange', () => {
      const prev = this.route;
      this.route = parseRoute(location.hash);
      const a = this.route.query.get('area');
      if (this.route.name === 'home' && a !== this.area && this.route.query.has('area')) this.area = a;
      if (replayFromQuery(prev.query) !== this.replay) this.onDatasetChange();
      else if (this.route.query.get('at') !== prev.query.get('at') && !this.playing) this.clock = null;
    });
    window.setInterval(() => {
      this.now = this.store.now();
      const st = this.store.staleInfo;
      if (JSON.stringify(st) !== JSON.stringify(this.data.stale)) { holdScroll(); this.data.stale = st; }
    }, 15_000);
    // the countdown: tick every second until polls close (and once past it), then stop re-rendering
    this.tick = live.store.now();
    window.setInterval(() => {
      const t = live.store.now(), pc = this.pollsClose;
      if (pc !== null && (t < pc || this.tick < pc)) {
        if (t >= pc && !this.replay) holdScroll(); // 7:00 PM: the countdown gives way (and any warnings may show)
        this.tick = t;
      }
    }, 1000);
    window.addEventListener('keydown', (e) => {
      // ←/→ step through the night — only as a plain key press on the page itself: never with a modifier
      // (Alt+← is browser Back), and never while focus is on anything interactive or scrollable
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || !this.points.length) return;
      const t = e.target as HTMLElement | null;
      if (t && t !== document.body && t !== document.documentElement) return;
      if (e.key === 'ArrowLeft') { this.step(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { this.step(1); e.preventDefault(); }
    });
    void this.probeReplays();
  }

  private async probeReplays() {
    const found: string[] = [];
    await Promise.all(REPLAY_DATASETS.map(async (id) => {
      try {
        const r = await fetch(`${datasetBase(id)}meta.json`, { method: 'HEAD', cache: 'no-store' });
        if (r.ok) found.push(id);
      } catch { /* offline */ }
    }));
    this.availableReplays = found.sort().reverse();
  }

  private onDatasetChange() {
    this.pause();
    this.clock = null;
    if (this.replay) replaySet(this.replay).start();
  }

  /** Switch between live (null) and a replay dataset. Keeps the page, drops the position. */
  switchDataset(id: string | null) {
    location.hash = switchDatasetHash(location.hash, id);
  }

  /** Link to a route, carrying the dataset and replay position so navigating keeps the same moment. */
  href(path: string): string {
    let h = path.startsWith('#') ? path : `#${path}`;
    if (this.replay) h = withHashParam(h, 'replay', this.replay);
    if (!this.isLive && this.at && this.viewSid) h = withHashParam(h, 'at', this.viewSid);
    return h;
  }

  /** Show a snapshot (null = default: live's latest / a replay's first). replaceState: scrubbing doesn't flood history. */
  setAt(sid: string | null, keepClock = false) {
    let v = sid;
    if (!this.replay) {
      const latest = this.points.at(-1)?.snapshot_id ?? this.liveSid;
      if (v === latest) v = null;
    }
    const h = withHashParam(location.hash || '#/', 'at', v);
    history.replaceState(null, '', h);
    this.route = parseRoute(h);
    if (!keepClock) this.clock = null;
  }

  // ------------------------------------------------------------------ player
  /** Jump the clock to a time; the view shows the latest snapshot at or before it. */
  seek(t: number) {
    const ts = this.times;
    if (!ts.length) return;
    const c = Math.max(ts[0]!, Math.min(ts.at(-1)!, t));
    const i = Math.max(0, indexAtOrBefore(ts, c));
    this.setAt(this.points[i]!.snapshot_id, true);
    this.clock = c;
  }

  step(dir: -1 | 1) {
    const ts = this.times;
    if (!ts.length) return;
    this.pause();
    const i = stepIndex(ts, this.displayTime ?? ts.at(-1)!, dir);
    this.seek(ts[i]!);
  }

  play() {
    const ts = this.times;
    if (ts.length < 2) return;
    // from the start if at the end (or at live's latest), else resume from the clock
    const from = this.atEnd || this.isLive ? ts[0]! : (this.displayTime ?? ts[0]!);
    this.seek(from);
    this.playing = true;
    this.last = performance.now();
    this.prefetch(this.viewIndex);
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame((n) => this.frame(n));
  }

  pause() {
    this.playing = false;
    cancelAnimationFrame(this.raf);
  }

  /** Back to the newest results (live) or the end of the night (replay). */
  toEnd() {
    this.pause();
    if (this.replay) this.seek(this.times.at(-1) ?? 0);
    else this.setAt(null);
  }

  private frame(now: number) {
    if (!this.playing) return;
    const dt = Math.min(250, now - this.last);
    this.last = now;
    const ts = this.times;
    const r = playbackStep(ts, this.clock ?? ts[0]!, dt, this.speed);
    const p = this.points[r.index];
    if (p && p.snapshot_id !== this.viewSid) {
      this.setAt(p.snapshot_id, true);
      this.prefetch(r.index);
    }
    this.clock = r.clock;
    if (r.done) {
      this.playing = false;
      // live: the newest snapshot is "live" again; a replay stays at the end of the night
      if (!this.replay) { this.setAt(null); this.clock = null; }
      return;
    }
    this.raf = requestAnimationFrame((n) => this.frame(n));
  }

  private prefetch(i: number) {
    const route = this.route;
    for (const sid of prefetchIds(this.points, i, 2)) {
      this.store.overview(sid).catch(() => {});
      if (route.name === 'race' && route.id) this.store.race(sid, route.id).catch(() => {});
      if (route.name === 'turnout') this.store.turnout(sid).catch(() => {});
    }
  }

  setArea(area: string | null) {
    this.area = area;
    ls.set(AREA_KEY, area);
    if (this.route.name === 'home') {
      const h = withHashParam(location.hash || '#/', 'area', area);
      history.replaceState(null, '', h);
      this.route = parseRoute(h);
    }
  }

  setTheme(t: Theme) {
    this.theme = t;
    ls.set(THEME_KEY, t === 'auto' ? null : t);
    if (t === 'auto') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = t;
  }
}

export const app = new App();

/**
 * Where the night is for the live dataset (never in replays): 'countdown' until polls close (unless votes are
 * somehow already counted, e.g. test data), 'closed' after 7 PM until the first votes are counted, else null.
 */
export function pollPhase(ov: Pick<Overview, 'races'> | null): 'countdown' | 'closed' | null {
  if (app.replay || app.pollsClose === null) return null;
  const waiting = isPreResults(app.meta, ov?.races ?? null);
  if (app.tick < app.pollsClose) return waiting || !ov ? 'countdown' : null;
  return waiting ? 'closed' : null;
}

/** The active dataset's store (live or replay). Components call `ds().overview(...)` etc. */
export const ds = (): Store => app.store;

/**
 * Load something that depends on reactive state; keeps the last good value on failure and re-checks on every
 * meta poll (immutable files come straight from the in-memory cache, so that is cheap). When the dataset changes
 * the old value is dropped. Must be called during component initialisation.
 */
export function resource<T>(fn: () => Promise<T> | null | undefined) {
  let value = $state.raw<T | null>(null);
  let error = $state.raw<unknown>(null);
  let loading = $state(true);
  let lastData: unknown = null;
  $effect(() => {
    void app.poll;
    const data = app.data;
    if (data !== lastData) { value = null; lastData = data; }
    const p = fn();
    if (!p) { loading = false; return; }
    let alive = true;
    loading = true;
    p.then((v) => {
      if (!alive) return;
      if (v !== value) holdScroll(); // new numbers can change heights above the reader
      value = v; error = null; data.noteOk();
    }, (e) => { if (alive) { error = e; data.noteError(); } })
      .finally(() => { if (alive) loading = false; });
    return () => { alive = false; };
  });
  return {
    get value() { return value; },
    get error() { return error; },
    get loading() { return loading; },
  };
}
